import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Grid,
  Button,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  userManagementGetById,
  impersonateUserApi,
  mobileLoginCodeApi,
  logoutAllDevicesApi,
} from '../user.service';
import Loader from '../../../../components/loader';
import NotificationSnackbar from '../../../../components/notificationSnackbar';
import MobileLoginCodeDialog from '../../../../components/mobileLoginCodeDialog';
import {
  detailFieldSx,
  detailLabelSx,
  detailPageCardSx as pageCardSx,
  detailSectionTitleSx,
  detailValueSx,
} from '../../../../styles/detailStyles';
import StatusChip from '../../../../components/statusChip';
import DareApprovalSection from './dareApprovalSection';
import { personaLabel as formatPersona } from '../../../../constant/personaLabel';

function DetailField({ label, value, fullWidth = false, renderValue, minHeight }) {
  return (
    <Grid size={{ xs: 12, md: fullWidth ? 12 : 6 }}>
      <Box sx={{ ...detailFieldSx, minHeight: minHeight || detailFieldSx.minHeight }}>
        <Typography sx={detailLabelSx}>
          {label}
        </Typography>
        {renderValue ? (
          renderValue()
        ) : (
          <Typography sx={detailValueSx}>
            {value || '-'}
          </Typography>
        )}
      </Box>
    </Grid>
  );
}

const UserView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState({});
  const [loading, setLoading] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [mobileCodeLoading, setMobileCodeLoading] = useState(false);
  const [mobileCode, setMobileCode] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Reuse a still-valid code instead of issuing a new one (which would invalidate it).
  const codeCacheRef = useRef({});

  const handleMobileLoginCode = async () => {
    const cached = codeCacheRef.current[id];
    if (cached?.expiresAt && new Date(cached.expiresAt).getTime() > Date.now()) {
      setMobileCode(cached);
      return;
    }
    setMobileCodeLoading(true);
    const res = await mobileLoginCodeApi(id);
    setMobileCodeLoading(false);

    if (res?.statusCode === 200 && res.data?.code) {
      codeCacheRef.current[id] = res.data;
      setMobileCode(res.data);
    } else {
      setSnackbar({ open: true, message: res?.message || 'Unable to generate a mobile login code', severity: 'error' });
    }
  };

  const handleImpersonate = async () => {
    setImpersonating(true);
    const res = await impersonateUserApi(id);
    setImpersonating(false);

    if (res?.statusCode === 200 && res.data?.code && res.data?.redirectUrl) {
      const { redirectUrl, code, userId } = res.data;
      const sep = redirectUrl.includes('?') ? '&' : '?';
      const url = `${redirectUrl}${sep}code=${encodeURIComponent(code)}&userId=${encodeURIComponent(userId)}`;
      // Open the target persona app in a new tab; it exchanges the code for a session.
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      setSnackbar({ open: true, message: res?.message || 'Unable to log in as this user', severity: 'error' });
    }
  };

  const handleLogoutAllDevices = async () => {
    setConfirmLogout(false);
    setLoggingOut(true);
    const res = await logoutAllDevicesApi(id);
    setLoggingOut(false);

    if (res?.statusCode === 200) {
      // Zero is a real answer, not a failure — the user simply had nothing live. Say so
      // plainly, otherwise "Signed out of 0 devices" reads as though the action failed.
      const count = res.data?.revokedSessions ?? 0;
      setSnackbar({
        open: true,
        message:
          count === 0
            ? 'This user had no active sessions. Nothing to sign out.'
            : `Signed out of ${count} device${count === 1 ? '' : 's'}. The user will be asked for a new OTP.`,
        severity: 'success',
      });
    } else {
      setSnackbar({ open: true, message: res?.message || "Unable to end this user's sessions", severity: 'error' });
    }
  };

  const getUserManagementData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await userManagementGetById(id);
      setUserData(data?.data || {});
    } catch (error) {
      console.error('Error fetching user type by ID', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    getUserManagementData();
  }, [getUserManagementData]);

  return (
    <Box sx={{ width: '100%', maxWidth: { xs: '100%', lg: 1360, xl: 1280 }, boxSizing: 'border-box', mx: 'auto', px: { xs: 0, lg: 3, xl: 4 } }}>
      <Grid container alignItems="center" mb={4} rowSpacing={2} columnSpacing={2}>
        <Grid size={{ xs: 12, md: 3 }} textAlign={{ xs: 'left', md: 'left' }}>
          <Button
            onClick={() => navigate('/user')}
            startIcon={<span className="material-symbols-rounded">arrow_back_ios_new</span>}
            sx={{
              minWidth: 'auto',
              px: 0,
              color: '#1C1B1F',
              textTransform: 'none',
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              fontWeight: 500,
              '& .material-symbols-rounded': {
                fontSize: 18,
              },
            }}
          >
            Back
          </Button>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} order={{ xs: 3, md: 2 }}>
          <Typography
            className="app-page-title"
            sx={{
              textAlign: 'center',
              fontSize: { xs: 28, sm: 30, md: 34 },
              lineHeight: { xs: '34px', sm: '36px', md: '40px' },
              letterSpacing: '-0.25px',
              fontWeight: 700,
              color: '#1C1B1F',
            }}
          >
            User Onboarding Details
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }} order={{ xs: 2, md: 3 }} textAlign={{ xs: 'left', md: 'right' }}>
          {/* Four actions wrap in this column at md instead of overflowing.
              useFlexGap is required or Stack's margin-based spacing breaks on the wrapped row. */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
          >
            <Button
              onClick={() => navigate(`/editUser/${id}`)}
              disabled={!userData?.id}
              startIcon={<span className="material-symbols-rounded">edit</span>}
              sx={{
                minWidth: { xs: '100%', sm: 86 },
                height: 40,
                borderRadius: '12px',
                backgroundColor: '#DFE5F6',
                color: '#294190',
                textTransform: 'none',
                fontSize: 16,
                lineHeight: '24px',
                letterSpacing: '0.15px',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: '#DFE5F6',
                },
                '& .material-symbols-rounded': {
                  fontSize: 18,
                },
              }}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              onClick={handleImpersonate}
              disabled={impersonating || !userData?.id}
              startIcon={<span className="material-symbols-rounded">login</span>}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {impersonating ? 'Opening…' : 'Log in as user'}
            </Button>
            {['AGENT', 'FARMER'].includes((userData?.persona || '').toUpperCase()) && (
              <Button
                variant="outlined"
                onClick={handleMobileLoginCode}
                disabled={mobileCodeLoading || !userData?.id}
                startIcon={<span className="material-symbols-rounded">smartphone</span>}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {mobileCodeLoading ? 'Generating…' : 'Mobile login code'}
              </Button>
            )}
            <Button
              variant="outlined"
              color="error"
              onClick={() => setConfirmLogout(true)}
              disabled={loggingOut || !userData?.id}
              startIcon={<span className="material-symbols-rounded">logout</span>}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {loggingOut ? 'Signing out…' : 'Log out all devices'}
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Box sx={pageCardSx}>
        <Typography sx={detailSectionTitleSx}>
          Applicant Details
        </Typography>

        <Grid container spacing={2.25}>
          <DetailField label="Full Name" value={userData?.fullName} />
          <DetailField label="Email" value={userData?.email} />
          <DetailField label="Country Code" value={userData?.countryCode} />
          <DetailField label="Phone Number" value={userData?.phoneNumber} />
          <DetailField label="User Persona" value={formatPersona(userData?.persona)} />
          <DetailField label="Country" value={userData?.countryName || userData?.country} />
          <DetailField label="Administrative Region" value={userData?.administrativeRegionName} />
          <DetailField label="Administrative Unit Name" value={userData?.administrativeUnitName} />
          <DetailField label="Last Mile Administrative Unit" value={userData?.lastMileUnitName} />
          <DetailField label="Block" value={userData?.block} />
          <DetailField label="Pincode" value={userData?.pincode} />
          <DetailField label="Payment Method" value={userData?.preferredPaymentMethod} />
          <DetailField label="Initiative" value={userData?.initiativeName} />
          <DetailField label="Subscription Plan" value={userData?.subscriptionPlanName} />
          <DetailField label="Ecosystem Partner" value={userData?.ecosystemIntegrator} />
          <DetailField
            label="Status"
            renderValue={() => (
              <StatusChip status={userData?.status} />
            )}
          />
        </Grid>
      </Box>

      {(userData?.persona || '').toUpperCase() === 'DARE' && userData?.id ? (
        <DareApprovalSection
          dareUserId={userData.id}
          dareName={userData.fullName}
          notify={(message, severity = 'success') => setSnackbar({ open: true, message, severity })}
        />
      ) : null}

      {loading && <Loader />}

      <Dialog
        open={confirmLogout}
        onClose={() => setConfirmLogout(false)}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 420,
            borderRadius: '24px',
            p: 1,
            mx: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5, fontSize: 24, lineHeight: '32px', letterSpacing: 0, fontWeight: 700, color: '#1C1B1F' }}>
          Log out all devices
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>
            End every session for {userData?.fullName || 'this user'}?
          </Typography>
          <Typography sx={{ mt: 1.5, fontSize: 14, lineHeight: '20px', letterSpacing: '0.1px', color: '#6C6A86' }}>
            They will be signed out of every device and app, and will need a new OTP to log back
            in. Use this when a phone has been lost or stolen. Anything they are part-way through
            on another device will be lost.
          </Typography>
          <Typography sx={{ mt: 1.5, fontSize: 14, lineHeight: '20px', letterSpacing: '0.1px', color: '#6C6A86' }}>
            A device that is already open can stay usable for up to 15 more minutes before it is
            asked to renew. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, justifyContent: 'flex-end', gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' }, alignItems: 'stretch' }}>
          <Button
            onClick={() => setConfirmLogout(false)}
            sx={{
              minWidth: 88,
              height: 40,
              borderRadius: '10px',
              textTransform: 'none',
              color: '#6C6A86',
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              fontWeight: 500,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutAllDevices}
            variant="contained"
            sx={{
              minWidth: 100,
              height: 40,
              borderRadius: '10px',
              textTransform: 'none',
              bgcolor: '#DA342E',
              color: '#FFFFFF',
              boxShadow: 'none',
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              fontWeight: 500,
              width: { xs: '100%', sm: 'auto' },
              '&:hover': { bgcolor: '#DA342E', boxShadow: 'none' },
            }}
          >
            Log out all devices
          </Button>
        </DialogActions>
      </Dialog>

      <MobileLoginCodeDialog
        codeData={mobileCode}
        onClose={() => setMobileCode(null)}
        onCopied={() => setSnackbar({ open: true, message: 'Code copied', severity: 'success' })}
      />

      <NotificationSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
};

export default UserView;
